import classNames from 'classnames'
import { useMemo, useState } from 'react'
import Button from '../../components/Button'
import Card from '../../components/Card'
import Input from '../Onboarding/Input'

const SUPPORT_CALL_URL = 'https://discord.com/channels/1187941664841535559/1187942865846927500'
const DISCOUNT_URL = `https://discord.com/channels/1187941664841535559/1187942865846927500`
const STRIPE_CANCEL_URL = 'https://billing.stripe.com/p/login/6oUeVcb8je4ObW431peZ202'

const FLOW_COMPONENTS = {
  price: PriceFlow,
  competition: CompetitionFlow,
  'missing-features': MissingFeaturesFlow,
  'technical-issues': TechnicalIssuesFlow,
  other: OtherReasonFlow,
}

export default function ChurnReasonFlow({ reason, onBackToReasons, onComplete }) {
  let FlowComponent = useMemo(() => FLOW_COMPONENTS[reason?.id], [reason])
  if (!reason || !FlowComponent) return null

  return (
    <FlowComponent
      reason={reason}
      onBackToReasons={onBackToReasons}
      onComplete={onComplete}
    />
  )
}

let redirectTo = (url) => {
  window.location.href = url
}

let completeAndRedirect = (onComplete, data, url, { meta } = {}) => {
  let payload = {
    ...data,
    meta: {
      preventReset: true,
      ...meta,
    },
  }

  try {
    let result = onComplete(payload)
    if (result && typeof result.then === 'function') {
      result.catch((error) => {
        console.error('Failed to send churn data before redirect', error)
      })
    }
  } catch (error) {
    console.error('Failed to trigger churn completion before redirect', error)
  }

  redirectTo(url)
}

function FlowContainer({ title, description, children, onBack, showBack = true }) {
  return (
    <div className='flex flex-col w-full h-full items-center justify-center px-4'>
      <Card withGradient className='relative overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.45)]'>
        <div className='px-12 md:px-20 py-12 flex flex-col gap-8 text-white w-[560px] md:w-[760px] max-w-full relative'>
          {showBack && (
            <Button
              className='absolute top-6 right-6 px-5 py-2 rounded-full bg-white/5 text-neutral-400 text-xs font-medium border border-white/10 flex items-center justify-center'
              onClick={onBack}
            >
              Zurück
            </Button>
          )}
          <div className='flex flex-col gap-3'>
            <div className='text-sm uppercase tracking-[0.2em] text-neutral-600'>
              Edge Support
            </div>
            <div className='text-3xl font-bold leading-snug drop-shadow-[0_6px_20px_rgba(13,217,156,0.35)]'>
              {title}
            </div>
            {description && (
              <div className='text-neutral-300 text-base leading-relaxed max-w-[620px]'>
                {description}
              </div>
            )}
          </div>

          <div className='flex flex-col gap-6 pt-2'>
            {children}
          </div>
        </div>
      </Card>
    </div>
  )
}

function PriceFlow({ onBackToReasons, onComplete }) {
  let [step, setStep] = useState(0)
  let [reasonDetail, setReasonDetail] = useState('')
  let [desiredPrice, setDesiredPrice] = useState('')
  let [farewell, setFarewell] = useState(null)

  let handleBack = () => {
    if (step === 0) {
      onBackToReasons()
    } else if (step === 3) {
      setFarewell(null)
      setStep(2)
    } else {
      setStep((value) => Math.max(0, value - 1))
    }
  }

  let confirmFarewell = () => {
    if (!farewell) return
    completeAndRedirect(onComplete, farewell.payload, farewell.url)
  }

  if (step === 0) {
    let options = [
      { value: 'kein_kapital', label: 'Mir fehlt aktuell das Kapital.' },
      { value: 'abo_teuer', label: 'Das Abo ist mir zu teuer.' },
    ]

    return (
      <FlowContainer
        title={(
          <>
            <span className='text-emerald-400'>Warum</span> empfindest du das Abo als zu teuer?
          </>
        )}
        description='Sag uns kurz, was dich aktuell am meisten beschäftigt. Dann schauen wir gemeinsam nach einer Lösung.'
        onBack={handleBack}
      >
        <div className='flex flex-col gap-4'>
          {options.map((option) => (
            <label
              key={option.value}
              className='border border-neutral-800 rounded-2xl px-6 py-4 text-neutral-200 cursor-pointer flex items-center gap-3'
            >
              <input
                type='radio'
                name='price-reason'
                value={option.value}
                checked={reasonDetail === option.value}
                onChange={(event) => setReasonDetail(event.target.value)}
                className='w-4 h-4 accent-primary'
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
        <Button
          variant='secondary'
          className='self-start px-10 py-3 text-primary font-bold'
          disabled={!reasonDetail}
          onClick={() => reasonDetail && setStep(1)}
        >
          Weiter
        </Button>
      </FlowContainer>
    )
  }

  if (step === 1) {
    return (
      <FlowContainer
        title={
          <>
            Exklusives Angebot:
            <br />
            Spare <span className='text-emerald-400'>50%</span> für 2 Monate
          </>
        }
        description='Wir schätzen deine Ehrlichkeit und können das vollkommen verstehen. Genau aus diesem Grund ist die Kündigung nicht die beste Lösung. Statt deine bisherige Investition an Zeit komplett aufzugeben, ermöglichen wir dir eine elegante Überbrückung.'
        onBack={handleBack}
      >
        <div className='flex gap-4 flex-wrap'>
          <Button
            variant='secondary'
            size='lg'
            className='bg-gradient-to-r from-emerald-300 to-emerald-500 hover:from-emerald-200 hover:to-emerald-400 !text-black hover:!text-black font-semibold px-8 py-3 rounded-full shadow-[0_10px_30px_rgba(13,217,156,0.3)]'
            onClick={() => {
              completeAndRedirect(onComplete, {
                outcome: 'discount_accepted',
                reason: 'price',
                responses: { cause: reasonDetail, acceptedDiscount: true },
              }, DISCOUNT_URL)
            }}
          >
            Angebot sichern
          </Button>
          <Button
            size='md'
            className='bg-neutral-950 text-neutral-500 text-xs font-normal tracking-wide px-5 py-2 rounded-md border border-neutral-900 shadow-none opacity-80 flex items-center justify-center text-center'
            onClick={(event) => {
              event.preventDefault()
              setStep(2)
            }}
          >
            Kündigen
          </Button>
        </div>
      </FlowContainer>
    )
  }

  if (step === 2) {
    return (
      <FlowContainer
        title={(
          <>
            Welcher Preis würde für dich <span className='text-emerald-400'>passen</span>?
          </>
        )}
        description='Teile uns mit, welcher Preis für dich fair wäre. Wir melden uns bei dir, sobald wir ein passendes Angebot haben.'
        onBack={handleBack}
      >
        <Input
          title='Gewünschter Preis'
          placeholder='z. B. 49€ pro Monat'
          className='!font-normal !text-white placeholder:text-neutral-700 placeholder:opacity-60'
          value={desiredPrice}
          onChange={(event) => setDesiredPrice(event.target.value)}
        />
        <Button
          variant='secondary'
          className='self-start px-10 py-3 text-primary font-bold'
          disabled={!desiredPrice.trim()}
          onClick={() => {
            if (!desiredPrice.trim()) return
            setFarewell({
              payload: {
                outcome: 'cancel_requested',
                reason: 'price',
                responses: {
                  cause: reasonDetail,
                  acceptedDiscount: false,
                  desiredPrice: desiredPrice.trim(),
                },
              },
              url: STRIPE_CANCEL_URL,
            })
            setStep(3)
          }}
        >
          Weiter
        </Button>
      </FlowContainer>
    )
  }

  if (step === 3) {
    return (
      <FlowContainer
        title={(
          <div className='space-y-1'>
            <div><span className='text-primary'>Schade</span>, deine Kündigung ist bestätigt.</div>
            <div>Traurig dich gehen zu sehen!</div>
          </div>
        )}
        description={(
          <div className='space-y-4'>
            <div>Wichtig: Du verlierst nach Ablauf den gesamten Fortschritt und die exklusiven Preise.</div>
            <div>
              ✨ Deine 30-Tage-Garantie: Sofort 50% Rabatt auf die nächsten 2 Monate!<br />
              Deine Vorteile: Kein Datenverlust, Budget geschont, einmaliges Angebot
            </div>
          </div>
        )}
        onBack={handleBack}
      >
        <div className='flex flex-wrap gap-4'>
          <Button
            className='px-14 py-3 rounded-[22px] bg-gradient-to-r from-emerald-300 via-emerald-400 to-emerald-500 text-black font-extrabold text-lg shadow-[0_18px_55px_rgba(16,185,129,0.45)]'
            href={DISCOUNT_URL}
            onClick={() => {
              completeAndRedirect(onComplete, {
                outcome: 'discount_accepted',
                reason: 'price',
                responses: {
                  cause: reasonDetail,
                  acceptedDiscount: true,
                  desiredPrice: desiredPrice.trim(),
                },
              }, DISCOUNT_URL)
            }}
          >
            Rabatt sichern
          </Button>
          <Button
            className='px-7 py-2 rounded-[22px] bg-neutral-950 text-neutral-500 text-sm font-medium border border-neutral-900 opacity-80 flex items-center justify-center shadow-none text-center'
            disabled={!farewell}
            href={STRIPE_CANCEL_URL}
            onClick={confirmFarewell}
          >
            Angebot ablehnen
          </Button>
        </div>
      </FlowContainer>
    )
  }

  return null
}

function CompetitionFlow({ onBackToReasons, onComplete }) {
  let [step, setStep] = useState(0)
  let [competitor, setCompetitor] = useState('')
  let [why, setWhy] = useState('')
  let [changes, setChanges] = useState('')

  let handleBack = () => {
    if (step === 0) {
      onBackToReasons()
    } else {
      setStep((value) => Math.max(0, value - 1))
    }
  }

  if (step === 0) {
    return (
      <FlowContainer
        title={(
          <div className='space-y-1'>
            <div>Schade, dass du wechselst.</div>
            <div>
              Das solltest du davor <span className='text-primary'>wissen</span>
            </div>
          </div>
        )}
        description='Hilf uns dabei zu verstehen, welchen Anbieter du in Betracht ziehst. Einige Nutzer wechseln und kommen dann aufgrund der unvergleichbaren Leistung zurück zu Edge.'
        onBack={handleBack}
      >
        <Input
          title='Anbieter'
          placeholder='Name des Anbieters'
          className='!bg-black/40 text-neutral-200 placeholder:text-neutral-700 font-semibold'
          value={competitor}
          onChange={(event) => setCompetitor(event.target.value)}
          required
        />
        <Button
          variant='secondary'
          className='self-start px-10 py-3 text-primary font-bold'
          disabled={!competitor.trim()}
          onClick={() => competitor.trim() && setStep(1)}
        >
          Weiter
        </Button>
      </FlowContainer>
    )
  }

  if (step === 1) {
    return (
      <FlowContainer
        title={(
          <div className='space-y-1'>
            <div>Danke für deine Ehrlichkeit!</div>
            <div>
              Wir wollen die beste <span className='text-primary'>Lösung</span> für dich!
            </div>
          </div>
        )}
        description='Wir prüfen für dich den Anbieter & senden dir per Email einen Vergleich zu, damit du klar siehst wo du am meisten Leistung erhältst.'
        onBack={handleBack}
      >
        <Input
          title='Warum wechselst du?'
          type='textarea'
          className='min-h-[120px]'
          value={why}
          onChange={(event) => setWhy(event.target.value)}
          required
        />
        <Button
          variant='secondary'
          className='self-start px-10 py-3 text-primary font-bold'
          disabled={!why.trim()}
          onClick={() => why.trim() && setStep(2)}
        >
          Weiter
        </Button>
      </FlowContainer>
    )
  }

  if (step === 2) {
    return (
      <FlowContainer
        title='Was können wir ändern, damit du bleibst?'
        description='Gib uns ehrliches Feedback – wir möchten Edge stetig verbessern.'
        onBack={handleBack}
      >
        <Input
          title='Was sollen wir ändern?'
          type='textarea'
          className='min-h-[120px]'
          value={changes}
          onChange={(event) => setChanges(event.target.value)}
          required
        />
        <Button
          variant='secondary'
          className='self-start px-10 py-3 text-primary font-bold'
          disabled={!changes.trim()}
          onClick={() => changes.trim() && setStep(3)}
        >
          Weiter
        </Button>
      </FlowContainer>
    )
  }

  if (step === 3) {
    let finalResponses = {
      competitor: competitor.trim(),
      reason: why.trim(),
      requestedChanges: changes.trim(),
    }

    return (
      <FlowContainer
        title={(
          <div className='space-y-1'>
            <div><span className='text-primary'>Schade</span>, deine Kündigung ist bestätigt.</div>
            <div>Traurig dich gehen zu sehen!</div>
          </div>
        )}
        description={(
          <div className='space-y-4'>
            <div>Wichtig: Du verlierst nach Ablauf den gesamten Fortschritt und die exklusiven Preise.</div>
            <div>
              ✨ Deine 30-Tage-Garantie: Sofort 50% Rabatt auf die nächsten 2 Monate!<br />
              Deine Vorteile: Kein Datenverlust, Budget geschont, einmaliges Angebot
            </div>
          </div>
        )}
        onBack={handleBack}
      >
        <div className='flex flex-wrap gap-4'>
          <Button
            className='px-14 py-3 rounded-[22px] bg-gradient-to-r from-emerald-300 via-emerald-400 to-emerald-500 text-black font-extrabold text-lg shadow-[0_18px_55px_rgba(16,185,129,0.45)]'
            href={DISCOUNT_URL}
            onClick={() => {
              completeAndRedirect(onComplete, {
                outcome: 'discount_accepted',
                reason: 'competition',
                responses: { ...finalResponses, acceptedDiscount: true },
              }, DISCOUNT_URL)
            }}
          >
            Rabatt sichern
          </Button>
          <Button
            className='px-7 py-2 rounded-[22px] bg-neutral-950 text-neutral-500 text-sm font-medium border border-neutral-900 opacity-80 flex items-center justify-center shadow-none text-center'
            href={STRIPE_CANCEL_URL}
            onClick={() => {
              completeAndRedirect(onComplete, {
                outcome: 'cancel_confirmed',
                reason: 'competition',
                responses: { ...finalResponses, acceptedDiscount: false },
              }, STRIPE_CANCEL_URL)
            }}
          >
            Angebot ablehnen
          </Button>
        </div>
      </FlowContainer>
    )
  }

  return null
}

function MissingFeaturesFlow({ onBackToReasons, onComplete }) {
  let [step, setStep] = useState(0)
  let [wish, setWish] = useState('')
  let [farewell, setFarewell] = useState(null)

  let handleBack = () => {
    if (step === 0) {
      onBackToReasons()
    } else if (step === 2) {
      setFarewell(null)
      setStep(1)
    } else {
      setStep((value) => Math.max(0, value - 1))
    }
  }

  let confirmFarewell = () => {
    if (!farewell) return
    completeAndRedirect(onComplete, farewell.payload, farewell.url)
  }

  if (step === 0) {
    return (
      <FlowContainer
        title={(
          <>
            Welche <span className='text-emerald-400'>Funktionen</span> fehlen dir?
          </>
        )}
        description='Sag uns kurz, welche Features dir wichtig sind. Unser Team nutzt dein Feedback für die Roadmap.'
        onBack={handleBack}
      >
        <Input
          title='Feature-Wunsch'
          type='textarea'
          className='min-h-[140px]'
          value={wish}
          onChange={(event) => setWish(event.target.value)}
          required
        />
        <Button
          variant='secondary'
          className='self-start px-10 py-3 text-primary font-bold'
          disabled={!wish.trim()}
          onClick={() => wish.trim() && setStep(1)}
        >
          Weiter
        </Button>
      </FlowContainer>
    )
  }

  if (step === 1) {
    return (
      <FlowContainer
        title={(
          <>
            Exklusives Angebot:
            <br />
            Spare <span className='text-emerald-400'>50%</span> für 2 Monate
          </>
        )}
        description='Wir schätzen deine Ehrlichkeit und können das vollkommen verstehen. Genau aus diesem Grund ist die Kündigung nicht die beste Lösung. Statt deine bisherige Investition an Zeit komplett aufzugeben, ermöglichen wir dir eine elegante Überbrückung.'
        onBack={handleBack}
      >
        <div className='flex gap-4 flex-wrap'>
          <Button
            variant='secondary'
            size='lg'
            className='bg-gradient-to-r from-emerald-300 to-emerald-500 hover:from-emerald-200 hover:to-emerald-400 !text-black hover:!text-black font-semibold px-8 py-3 rounded-full shadow-[0_10px_30px_rgba(13,217,156,0.3)]'
            onClick={() => {
              completeAndRedirect(onComplete, {
                outcome: 'discount_accepted',
                reason: 'missing-features',
                responses: {
                  requestedFeatures: wish.trim(),
                  acceptedDiscount: true,
                },
              }, DISCOUNT_URL)
            }}
          >
            Angebot sichern
          </Button>
          <Button
            size='md'
            className='bg-neutral-950 text-neutral-500 text-xs font-normal tracking-wide px-5 py-2 rounded-md border border-neutral-900 shadow-none opacity-80 flex items-center justify-center text-center'
            onClick={(event) => {
              event.preventDefault()
              setFarewell({
                payload: {
                  outcome: 'cancel_confirmed',
                  reason: 'missing-features',
                  responses: {
                    requestedFeatures: wish.trim(),
                    acceptedDiscount: false,
                  },
                },
                url: STRIPE_CANCEL_URL,
              })
              setStep(2)
            }}
          >
            Ich spare nicht
          </Button>
        </div>
      </FlowContainer>
    )
  }

  if (step === 2) {
    return (
      <FlowContainer
        title={(
          <div className='space-y-1'>
            <div><span className='text-primary'>Schade</span>, deine Kündigung ist bestätigt.</div>
            <div>Traurig dich gehen zu sehen!</div>
          </div>
        )}
        description={(
          <div className='space-y-4'>
            <div>Wichtig: Du verlierst nach Ablauf den gesamten Fortschritt und die exklusiven Preise.</div>
            <div>
              ✨ Deine 30-Tage-Garantie: Sofort 50% Rabatt auf die nächsten 2 Monate!<br />
              Deine Vorteile: Kein Datenverlust, Budget geschont, einmaliges Angebot
            </div>
          </div>
        )}
        onBack={handleBack}
      >
        <div className='flex flex-wrap gap-4'>
          <Button
            className='px-14 py-3 rounded-[22px] bg-gradient-to-r from-emerald-300 via-emerald-400 to-emerald-500 text-black font-extrabold text-lg shadow-[0_18px_55px_rgba(16,185,129,0.45)]'
            href={DISCOUNT_URL}
            onClick={() => {
              completeAndRedirect(onComplete, {
                outcome: 'discount_accepted',
                reason: 'missing-features',
                responses: {
                  requestedFeatures: wish.trim(),
                  acceptedDiscount: true,
                },
              }, DISCOUNT_URL)
            }}
          >
            Rabatt sichern
          </Button>
          <Button
            className='px-7 py-2 rounded-[22px] bg-neutral-950 text-neutral-500 text-sm font-medium border border-neutral-900 opacity-80 flex items-center justify-center shadow-none text-center'
            disabled={!farewell}
            href={STRIPE_CANCEL_URL}
            onClick={confirmFarewell}
          >
            Angebot ablehnen
          </Button>
        </div>
      </FlowContainer>
    )
  }

  return null
}

function TechnicalIssuesFlow({ onBackToReasons, onComplete }) {
  let [step, setStep] = useState(0)
  let [issueDescription, setIssueDescription] = useState('')
  let [supportBooked, setSupportBooked] = useState(false)
  let [farewell, setFarewell] = useState(null)

  let handleBack = () => {
    if (step === 0) {
      onBackToReasons()
    } else if (step === 3) {
      setFarewell(null)
      setStep(2)
    } else {
      setStep((value) => Math.max(0, value - 1))
    }
  }

  let confirmFarewell = () => {
    if (!farewell) return
    completeAndRedirect(onComplete, farewell.payload, farewell.url)
  }

  if (step === 0) {
    return (
      <FlowContainer
        title={(
          <>
            Was <span className='text-emerald-400'>funktioniert</span> gerade nicht?
          </>
        )}
        description='Beschreibe kurz dein technisches Problem, damit wir gezielt helfen können.'
        onBack={handleBack}
      >
        <Input
          title='Problem'
          type='textarea'
          className='min-h-[140px]'
          value={issueDescription}
          onChange={(event) => setIssueDescription(event.target.value)}
          required
        />
        <Button
          variant='secondary'
          className='self-start px-10 py-3 text-primary font-bold'
          disabled={!issueDescription.trim()}
          onClick={() => issueDescription.trim() && setStep(1)}
        >
          Weiter
        </Button>
      </FlowContainer>
    )
  }

  if (step === 1) {
    return (
      <FlowContainer
        title={(
          <>
            Lass uns das <span className='text-emerald-400'>gemeinsam</span> besprechen
          </>
        )}
        description='Wir bieten dir einen kostenlosen Support-Call an, um dein Anliegen zu klären.'
        onBack={handleBack}
      >
        <div className='flex gap-4 flex-wrap'>
          <Button
            variant='secondary'
            size='lg'
            className='bg-gradient-to-r from-emerald-300 via-emerald-400 to-emerald-500 hover:from-emerald-200 hover:to-emerald-400 !text-black hover:!text-black font-semibold px-10 py-3 rounded-full shadow-[0_18px_45px_rgba(13,217,156,0.35)]'
            onClick={() => {
              setSupportBooked(true)
              completeAndRedirect(onComplete, {
                outcome: 'support_call',
                reason: 'technical-issues',
                responses: {
                  issueDescription: issueDescription.trim(),
                  supportCallBooked: true,
                  acceptedDiscount: false,
                },
              }, SUPPORT_CALL_URL)
            }}
          >
            Support-Call buchen
          </Button>
          <Button
            size='md'
            className='bg-neutral-950 text-neutral-500 text-xs font-normal tracking-wide px-5 py-2 rounded-md border border-neutral-900 shadow-none opacity-80 flex items-center justify-center text-center'
            onClick={(event) => {
              event.preventDefault()
              setSupportBooked(false)
              setFarewell(null)
              setStep(2)
            }}
          >
            Ablehnen
          </Button>
        </div>
      </FlowContainer>
    )
  }

  if (step === 2) {
    return (
      <FlowContainer
        title={(
          <>
            Exklusives Angebot:
            <br />
            Spare <span className='text-emerald-400'>50%</span> für 2 Monate
          </>
        )}
        description='Wir schätzen deine Ehrlichkeit und können das vollkommen verstehen. Genau aus diesem Grund ist die Kündigung nicht die beste Lösung. Statt deine bisherige Investition an Zeit komplett aufzugeben, ermöglichen wir dir eine elegante Überbrückung.'
        onBack={handleBack}
      >
        <div className='flex gap-4 flex-wrap'>
          <Button
            variant='secondary'
            size='lg'
            className='bg-gradient-to-r from-emerald-300 to-emerald-500 hover:from-emerald-200 hover:to-emerald-400 !text-black hover:!text-black font-semibold px-8 py-3 rounded-full shadow-[0_10px_30px_rgba(13,217,156,0.3)]'
            onClick={() => {
              completeAndRedirect(onComplete, {
                outcome: 'discount_accepted',
                reason: 'technical-issues',
                responses: {
                  issueDescription: issueDescription.trim(),
                  supportCallBooked: supportBooked,
                  acceptedDiscount: true,
                },
              }, DISCOUNT_URL)
            }}
          >
            Angebot sichern
          </Button>
          <Button
            size='md'
            className='bg-neutral-950 text-neutral-500 text-xs font-normal tracking-wide px-5 py-2 rounded-md border border-neutral-900 shadow-none opacity-80 flex items-center justify-center text-center'
            onClick={(event) => {
              event.preventDefault()
              setFarewell({
                payload: {
                  outcome: 'cancel_confirmed',
                  reason: 'technical-issues',
                  responses: {
                    issueDescription: issueDescription.trim(),
                    supportCallBooked: supportBooked,
                    acceptedDiscount: false,
                  },
                },
                url: STRIPE_CANCEL_URL,
              })
              setStep(3)
            }}
          >
            Ich spare nicht
          </Button>
        </div>
      </FlowContainer>
    )
  }

  if (step === 3) {
    return (
      <FlowContainer
        title={(
          <div className='space-y-1'>
            <div><span className='text-primary'>Schade</span>, deine Kündigung ist bestätigt.</div>
            <div>Traurig dich gehen zu sehen!</div>
          </div>
        )}
        description={(
          <div className='space-y-4'>
            <div>Wichtig: Du verlierst nach Ablauf den gesamten Fortschritt und die exklusiven Preise.</div>
            <div>
              ✨ Deine 30-Tage-Garantie: Sofort 50% Rabatt auf die nächsten 2 Monate!<br />
              Deine Vorteile: Kein Datenverlust, Budget geschont, einmaliges Angebot
            </div>
          </div>
        )}
        onBack={handleBack}
      >
        <div className='flex flex-wrap gap-4'>
          <Button
            className='px-14 py-3 rounded-[22px] bg-gradient-to-r from-emerald-300 via-emerald-400 to-emerald-500 text-black font-extrabold text-lg shadow-[0_18px_55px_rgba(16,185,129,0.45)]'
            href={DISCOUNT_URL}
            onClick={() => {
              completeAndRedirect(onComplete, {
                outcome: 'discount_accepted',
                reason: 'technical-issues',
                responses: {
                  issueDescription: issueDescription.trim(),
                  supportCallBooked: supportBooked,
                  acceptedDiscount: true,
                },
              }, DISCOUNT_URL)
            }}
          >
            Rabatt sichern
          </Button>
          <Button
            className='px-7 py-2 rounded-[22px] bg-neutral-950 text-neutral-500 text-sm font-medium border border-neutral-900 opacity-80 flex items-center justify-center shadow-none text-center'
            disabled={!farewell}
            href={STRIPE_CANCEL_URL}
            onClick={confirmFarewell}
          >
            Angebot ablehnen
          </Button>
        </div>
      </FlowContainer>
    )
  }

  return null
}

function OtherReasonFlow({ onBackToReasons, onComplete }) {
  let [step, setStep] = useState(0)
  let [reasonText, setReasonText] = useState('')
  let [supportBooked, setSupportBooked] = useState(false)
  let [farewell, setFarewell] = useState(null)

  let handleBack = () => {
    if (step === 0) {
      onBackToReasons()
    } else if (step === 3) {
      setFarewell(null)
      setStep(2)
    } else {
      setStep((value) => Math.max(0, value - 1))
    }
  }

  let confirmFarewell = () => {
    if (!farewell) return
    completeAndRedirect(onComplete, farewell.payload, farewell.url)
  }

  if (step === 0) {
    return (
      <FlowContainer
        title={(
          <>
            <span className='text-emerald-400'>Erzähl</span> uns kurz, warum du kündigen möchtest
          </>
        )}
        description='Mit deinem Feedback können wir Edge besser machen.'
        onBack={handleBack}
      >
        <Input
          title='Dein Grund'
          type='textarea'
          className='min-h-[140px]'
          value={reasonText}
          onChange={(event) => setReasonText(event.target.value)}
        />
        <Button
          variant='secondary'
          className={classNames(
            'self-start px-10 py-3 font-bold transition-colors duration-200',
            reasonText.trim()
              ? 'bg-secondary text-white'
              : '!bg-neutral-800 !text-neutral-300 hover:!bg-secondary hover:!text-white'
          )}
          onClick={() => setStep(1)}
        >
          Weiter
        </Button>
      </FlowContainer>
    )
  }

  if (step === 1) {
    return (
      <FlowContainer
        title={(
          <>
            Lass uns das <span className='text-emerald-400'>gemeinsam</span> besprechen
          </>
        )}
        description='Wir bieten dir einen kostenlosen Support-Call an, um dein Anliegen zu klären.'
        onBack={handleBack}
      >
        <div className='flex gap-4 flex-wrap'>
          <Button
            variant='secondary'
            size='lg'
            className='bg-gradient-to-r from-emerald-300 via-emerald-400 to-emerald-500 hover:from-emerald-200 hover:to-emerald-400 !text-black hover:!text-black font-semibold px-10 py-3 rounded-full shadow-[0_18px_45px_rgba(13,217,156,0.35)]'
            onClick={() => {
              setSupportBooked(true)
              completeAndRedirect(onComplete, {
                outcome: 'support_call',
                reason: 'other',
                responses: {
                  providedReason: reasonText.trim(),
                  supportCallBooked: true,
                  acceptedDiscount: false,
                },
              }, SUPPORT_CALL_URL)
            }}
          >
            Support-Call buchen
          </Button>
          <Button
            size='md'
            className='bg-neutral-950 text-neutral-500 text-xs font-normal tracking-wide px-5 py-2 rounded-md border border-neutral-900 shadow-none opacity-80 flex items-center justify-center text-center'
            onClick={(event) => {
              event.preventDefault()
              setSupportBooked(false)
              setStep(2)
            }}
          >
            Ablehnen
          </Button>
        </div>
      </FlowContainer>
    )
  }

  if (step === 2) {
    return (
      <FlowContainer
        title={(
          <>
            Exklusives Angebot:
            <br />
            Spare <span className='text-emerald-400'>50%</span> für 2 Monate
          </>
        )}
        description='Wir schätzen deine Ehrlichkeit und können das vollkommen verstehen. Genau aus diesem Grund ist die Kündigung nicht die beste Lösung. Statt deine bisherige Investition an Zeit komplett aufzugeben, ermöglichen wir dir eine elegante Überbrückung.'
        onBack={handleBack}
      >
        <div className='flex gap-4 flex-wrap'>
          <Button
            variant='secondary'
            size='lg'
            className='bg-gradient-to-r from-emerald-300 to-emerald-500 hover:from-emerald-200 hover:to-emerald-400 !text-black hover:!text-black font-semibold px-8 py-3 rounded-full shadow-[0_10px_30px_rgba(13,217,156,0.3)]'
            onClick={() => {
              completeAndRedirect(onComplete, {
                outcome: 'discount_accepted',
                reason: 'other',
                responses: {
                  providedReason: reasonText.trim(),
                  supportCallBooked: supportBooked,
                  acceptedDiscount: true,
                },
              }, DISCOUNT_URL)
            }}
          >
            Angebot sichern
          </Button>
          <Button
            size='md'
            className='bg-neutral-950 text-neutral-500 text-xs font-normal tracking-wide px-5 py-2 rounded-md border border-neutral-900 shadow-none opacity-80 flex items-center justify-center text-center'
            onClick={(event) => {
              event.preventDefault()
              setFarewell({
                payload: {
                  outcome: 'cancel_confirmed',
                  reason: 'other',
                  responses: {
                    providedReason: reasonText.trim(),
                    supportCallBooked: supportBooked,
                    acceptedDiscount: false,
                  },
                },
                url: STRIPE_CANCEL_URL,
              })
              setStep(3)
            }}
          >
            Ich spare nicht
          </Button>
        </div>
      </FlowContainer>
    )
  }

  if (step === 3) {
    return (
      <FlowContainer
        title={(
          <div className='space-y-1'>
            <div><span className='text-primary'>Schade</span>, deine Kündigung ist bestätigt.</div>
            <div>Traurig dich gehen zu sehen!</div>
          </div>
        )}
        description={(
          <div className='space-y-4'>
            <div>Wichtig: Du verlierst nach Ablauf den gesamten Fortschritt und die exklusiven Preise.</div>
            <div>
              ✨ Deine 30-Tage-Garantie: Sofort 50% Rabatt auf die nächsten 2 Monate!<br />
              Deine Vorteile: Kein Datenverlust, Budget geschont, einmaliges Angebot
            </div>
          </div>
        )}
        onBack={handleBack}
      >
        <div className='flex flex-wrap gap-4'>
          <Button
            className='px-14 py-3 rounded-[22px] bg-gradient-to-r from-emerald-300 via-emerald-400 to-emerald-500 text-black font-extrabold text-lg shadow-[0_18px_55px_rgba(16,185,129,0.45)]'
            href={DISCOUNT_URL}
            onClick={() => {
              completeAndRedirect(onComplete, {
                outcome: 'discount_accepted',
                reason: 'other',
                responses: {
                  providedReason: reasonText.trim(),
                  supportCallBooked: supportBooked,
                  acceptedDiscount: true,
                },
              }, DISCOUNT_URL)
            }}
          >
            Rabatt sichern
          </Button>
          <Button
            className='px-7 py-2 rounded-[22px] bg-neutral-950 text-neutral-500 text-sm font-medium border border-neutral-900 opacity-80 flex items-center justify-center shadow-none text-center'
            disabled={!farewell}
            href={STRIPE_CANCEL_URL}
            onClick={confirmFarewell}
          >
            Angebot ablehnen
          </Button>
        </div>
      </FlowContainer>
    )
  }

  return null
}
